import { accountEmail, isAccountEmail, isNormalized, normalizeEmail, } from "@pima/utils";
import { ParseResult } from "effect";
import * as S from "effect/Schema";
export const LanguageSchema = S.Literal("de-DE", "en-US");
export const UserLanguageSchema = S.Literal("de-DE", "en-GB", "en-US");
export const MeasureCategorySchema = S.Literal("gbpsych", "health", "safety");
export const MeasurePrioritySchema = S.Literal("high", "low", "medium");
export const RecurrenceSchema = S.Literal("half_yearly", "monthly", "quarterly", "two_yearly", "weekly", "yearly");
export const DueDateIntervalSchema = S.Literal("one_month", "one_week", "one_year", "six_months", "three_months", "two_weeks");
export const ContractTypeSchema = S.Literal("argumed", "dearest", "pima", "zap");
export const MeasureAssignmentStatusSchema = S.Literal("complete", "expired", "in_progress", "inactive", "not_started", "overdue");
export const RiskInstanceStatusSchema = S.Literal("accepted", "assigned", "draft", "instance", "mitigated", "rejected");
export const ExaminationFollowUpPeriodSchema = S.Literal("2-yearly", "3-yearly", "6-months", "monthly", "quarterly", "yearly");
const CsvStrings = S.transform(S.String, S.Array(S.String), {
    strict: true,
    decode: (value) => value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    encode: (values) => values.join(","),
});
/**
 * A comma-separated query parameter whose every item must be a member of
 * `item`. This is where a request string becomes a domain value: an unknown
 * member is a decode failure (400), never a value that reaches Postgres to
 * fail a column's type (500).
 */
export const csvOf = (item) => S.compose(CsvStrings, S.Array(item), { strict: false });
/**
 * An email address, folded to its identity key while decoding the request.
 *
 * Folding belongs here for the same reason every other schema in this file
 * does: it is where a request string becomes a domain value, and a handler that
 * folded its own would be a second rule competing with `@pima/utils`'
 * `normalizeEmail`. Decoding yields a `NormalizedEmail`, so a handler cannot
 * hold an unfolded address and a repository cannot be handed one.
 *
 * A string `normalizeEmail` rejects is not an address, and fails decoding — the
 * route answers 400 rather than storing something no login will ever match.
 */
export const NormalizedEmailSchema = S.transformOrFail(S.NonEmptyString, S.declare((input) => typeof input === "string" && isNormalized(input)), {
    strict: true,
    decode: (input, _options, ast) => {
        const key = normalizeEmail(input);
        return key === null
            ? ParseResult.fail(new ParseResult.Type(ast, input, `not an email address: "${input}"`))
            : ParseResult.succeed(key);
    },
    encode: (key) => ParseResult.succeed(key),
});
/**
 * A key that is already in normal form — validated, never folded.
 *
 * The counterpart to {@link NormalizedEmailSchema}, and the distinction is
 * about who is supposed to have folded. A person typing into a login box has
 * not, so the public edge folds for them. A caller on an internal boundary — a
 * value read back out of `normalized_email`, a payload from another service
 * that promises a key — is supposed to have folded already, and folding for
 * them a second time would quietly repair a caller that is wrong and leave the
 * two sides keying rows differently for as long as nobody looks.
 *
 * So this one refuses `Dana@Employer.test` rather than accepting it: the
 * mismatch surfaces where it is caused instead of where it is felt.
 */
export const NormalizedEmailKeySchema = S.typeSchema(NormalizedEmailSchema);
/**
 * An address an account can be created or found under, built while decoding
 * the request: folded to its identity key exactly as {@link NormalizedEmailSchema}
 * folds, and refused unless `isEmailAddress` accepts that key. Account writes
 * and lookups take the resulting `AccountEmail` and compare it byte for byte.
 */
export const AccountEmailSchema = S.transformOrFail(S.NonEmptyString, S.declare((input) => typeof input === "string" && isAccountEmail(input)), {
    strict: true,
    decode: (input, _options, ast) => {
        const key = accountEmail(input);
        return key === null
            ? ParseResult.fail(new ParseResult.Type(ast, input, `not an account email address: "${input}"`))
            : ParseResult.succeed(key);
    },
    encode: (key) => ParseResult.succeed(key),
});
/** An `AccountEmail` arriving already built: validated, never folded. */
export const AccountEmailKeySchema = S.typeSchema(AccountEmailSchema);
//# sourceMappingURL=column-schemas.js.map